import { SAFECARE_BILL_PARAMETERS } from './billParameters.js';

function clamp01(x) {
    if (Number.isNaN(x)) return 0;
    return Math.max(0, Math.min(1, x));
}

/**
 * Administrative overhead schedule (model assumption).
 * This is NOT specified as a numeric schedule in the bill text.
 *
 * It represents the transition path from today's blended system admin burden
 * toward a low-overhead steady state under uniform rules + a national platform.
 */
export function getAdminOverheadScheduleTargetRate(yearsActive) {
    // Year 0: 22% (dual systems)
    // Year 1: 20%
    // Year 2: 16%
    // Year 3: 12%
    // Year 4: 9%
    // Year 5: 7%
    // Year 6: 5%
    // Year 7: 3%
    // Year 8+: 2% (optimistic endpoint used by the schedule)
    if (yearsActive < 0) return 0.22;
    if (yearsActive === 0) return 0.22;
    if (yearsActive === 1) return 0.20;
    if (yearsActive === 2) return 0.16;
    if (yearsActive === 3) return 0.12;
    if (yearsActive === 4) return 0.09;
    if (yearsActive === 5) return 0.07;
    if (yearsActive === 6) return 0.05;
    if (yearsActive === 7) return 0.03;
    return 0.02;
}

/**
 * Effective admin overhead rate after enforcement realism + multipayer floor.
 *
 * - `enforceAdminSimplification` acts like "how much of the admin simplification regime is actually
 *   implemented" (0 → no meaningful simplification; 1 → full simplification).
 * - `adminEfficiencyFloor` clamps the rate upward to represent multipayer clearinghouse realism.
 */
export function getEffectiveAdminOverheadRate({ yearsActive, enforceAdminSimplification, adminEfficiencyFloor }) {
    const scheduleTarget = getAdminOverheadScheduleTargetRate(yearsActive);
    const enforcement = clamp01(enforceAdminSimplification ?? 1.0);

    // Interpret enforcement as the fraction of the achievable reduction that is realized.
    // If enforcement = 0, rate remains at current dual-system overhead (~22%).
    const currentSystemOverhead = 0.22;
    const enforcedRate = scheduleTarget + (1 - enforcement) * (currentSystemOverhead - scheduleTarget);

    const floor = adminEfficiencyFloor ?? 0;
    return Math.max(enforcedRate, floor);
}

export function getDrugCeilingInputs(config) {
    const mode = config.drugPricingMode || 'bill';
    const irpMultiplier = SAFECARE_BILL_PARAMETERS.drugPricing?.irpMultiplier ?? 1.10;
    const intlPct = config.internationalPricePctOfUS ?? 0.40;
    const dvbPct = config.domesticValuePricePctOfUS ?? 0.65;

    const irpPricePctOfUS = Math.min(1.0, Math.max(0, intlPct) * irpMultiplier);
    const dvbPricePctOfUS = Math.min(1.0, Math.max(0, dvbPct));
    const billPricePctOfUS = Math.min(irpPricePctOfUS, dvbPricePctOfUS);

    return { mode, irpMultiplier, intlPct, dvbPct, irpPricePctOfUS, dvbPricePctOfUS, billPricePctOfUS };
}

/**
 * Apply enforcement to a price ceiling.
 *
 * pricePctOfUS:
 *  - 1.0 means "no savings vs US"
 *  - 0.7 means "30% savings vs US"
 *
 * enforcement:
 *  - 1.0 means the ceiling is fully enforced
 *  - 0.0 means the ceiling has no practical effect
 */
export function applyCeilingEnforcement(pricePctOfUS, enforcement) {
    const p = clamp01(pricePctOfUS);
    const e = clamp01(enforcement);
    return 1.0 - e * (1.0 - p);
}

/**
 * Effective drug ceiling implied by the bill's min(IRP, DVB) structure,
 * with explicit scenario control for legal/political enforceability.
 *
 * - `enforceIRP` applies to the IRP component.
 * - `enforceDrugCeilings` applies to the DVB/statutory ceiling machinery.
 *
 * NOTE: This is still an aggregate proxy. The bill defines per-drug ceilings; the simulator
 * collapses that into a single "price-as-%-of-US" anchor for brand spending.
 */
export function getEffectiveDrugCeilingPricePctOfUS(config) {
    const { mode, irpPricePctOfUS, dvbPricePctOfUS } = getDrugCeilingInputs(config);
    const enforceIRP = clamp01(config.enforceIRP ?? 1.0);
    const enforceDVB = clamp01(config.enforceDrugCeilings ?? 1.0);

    const effectiveIRP = applyCeilingEnforcement(irpPricePctOfUS, enforceIRP);
    const effectiveDVB = applyCeilingEnforcement(dvbPricePctOfUS, enforceDVB);

    if (mode === 'irp_only') return { mode, effectivePricePctOfUS: effectiveIRP, effectiveIRP, effectiveDVB };
    if (mode === 'dvb_only') return { mode, effectivePricePctOfUS: effectiveDVB, effectiveIRP, effectiveDVB };

    // 'bill' default: Statutory ceiling is the lower of the two ceilings.
    return { mode: 'bill', effectivePricePctOfUS: Math.min(effectiveIRP, effectiveDVB), effectiveIRP, effectiveDVB };
}

